import { Button, DatePicker, Form, Input, Modal, Spin } from 'antd';
import React, { useEffect } from 'react';
import useSWR, { type KeyedMutator } from 'swr';

import { CYCLE_TYPE_OPTIONS, FORM_INITIAL_VALUES } from '../../constants';
import type { InspectionTableResponse, RecordInTable } from '../../types';
import CycleDaysField from './components/CycleDaysField';
import SelectorField from './components/SelectorField';
import TimeRangeListField from './components/TimeRangeListField';
import {
  fetchFormOptions,
  getFormFieldsValue,
  getSelectorFieldProps,
  onFinish,
} from './helpers';
import styles from './index.module.scss';
import type { FormValues } from './types';

type RouteFormModalProps = {
  open: boolean;
  closeModal: () => void;
  formType: 'add' | 'edit';
  editingRoute: RecordInTable | undefined;
  revalidateRouteTableData: KeyedMutator<InspectionTableResponse | undefined>;
};

const DateRangePicker = DatePicker.RangePicker;

const RouteFormModal = ({
  open,
  closeModal,
  formType,
  editingRoute,
  revalidateRouteTableData,
}: RouteFormModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const cycleType = Form.useWatch(['cycle', 'type'], form);
  const slot = Form.useWatch('slot', form);

  const { data: formOptions, isLoading } = useSWR(
    open ? '/ezcon/api/machineInspectionPlans/createFormOptions' : null,
    fetchFormOptions
  );

  useEffect(() => {
    if (formType === 'edit' && editingRoute) {
      const newFieldsValue = getFormFieldsValue(editingRoute);
      form.setFieldsValue(newFieldsValue);
    }
  }, [editingRoute, form, formType]);

  const onClose = () => {
    form.resetFields();
    closeModal();
  };

  const onValuesChange = (changedValues: FormValues) => {
    if (changedValues.cycle && changedValues.cycle.type) {
      form.setFieldValue(['cycle', 'days'], []);
    }
  };

  const selectorFieldProps = getSelectorFieldProps(formOptions);

  return (
    <Modal
      open={open}
      title={formType === 'add' ? '新增路線' : '編輯路線'}
      centered
      footer={null}
      destroyOnClose
      onCancel={onClose}
    >
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      ) : (
        <Form
          form={form}
          onFinish={(formValues) =>
            onFinish({
              formValues:
                formType === 'edit' && editingRoute
                  ? { ...formValues, id: editingRoute.id }
                  : formValues,
              formType,
              setFields: form.setFields,
              onClose,
              revalidateRouteTableData,
            })
          }
          labelAlign="left"
          labelCol={{ span: 4 }}
          initialValues={FORM_INITIAL_VALUES}
          onValuesChange={onValuesChange}
        >
          <Form.Item
            label="路線"
            name="route"
            rules={[{ required: true, message: '請輸入路線名稱' }]}
          >
            <Input type="text" placeholder="請輸入路線名稱" />
          </Form.Item>
          {selectorFieldProps.map((props, index) => (
            <SelectorField key={index} {...props} />
          ))}
          <Form.Item
            label="期間"
            name="period"
            rules={[{ required: true, message: '請選擇期間' }]}
          >
            <DateRangePicker />
          </Form.Item>
          <SelectorField
            label="週期"
            name={['cycle', 'type']}
            rules={[{ required: true, message: '請選擇週期' }]}
            placeholder="請選擇週期"
            options={CYCLE_TYPE_OPTIONS}
          />
          <CycleDaysField cycleType={cycleType} />
          <TimeRangeListField slot={slot} />
          <div className={styles.footerButtons}>
            <Button type="primary" htmlType="submit">
              確定
            </Button>
            <Button type="default" onClick={onClose}>
              取消
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default RouteFormModal;
